import { createComponent } from '@carvana/merch-components';
import cx from 'classnames';
import {
  type ButtonProps,
  PrimaryButton,
  SecondaryButton
} from '@/common/Button';
import { DefaultFinanceCard } from '@/common/FinanceCard';
import { FinanceCardType } from '@/common/FinanceCard/constants';
import { getFinanceCardType } from '@/common/FinanceCard/utils';
import { FinanceCard as CommonCard } from '~/components/common/FinanceCard';
import { useIsLoggedIn } from '~/context/common/UserContext';
import {
  useHasExpiredSoftPull,
  useHasSoftPull,
  useIsFinanceInitialized
} from '~/context/srp/FinanceContext';
import { useDaysToExpired } from '~/context/srp/FinanceContext/FinanceContext';
import {
  SoftPullLocations,
  useOpenSoftPullModal
} from '~/hooks/srp/useToggleSoftPullModal';

const LoadingBlock = createComponent('div', {
  className: 'animate-pulse bg-background-skeleton -:rounded-[6px]'
});

function LoadingCard() {
  return (
    <div className="rounded-md border border-solid border-grey-2 p-24 -:bg-white">
      <div
        className="flex flex-col gap-16"
        data-qa="finance-card-pre-qualified"
      >
        <div className="flex flex-row justify-between">
          <LoadingBlock className="h-24 w-1/2" />
          <LoadingBlock className="h-24 w-1/3" />
        </div>

        <div className="flex w-full flex-row gap-x-16">
          <div className="h-[60px] w-[60px] animate-pulse rounded-full bg-background-skeleton" />

          <div className="flex h-[92px] w-full flex-1 flex-col gap-8">
            <LoadingBlock className="flex-1" />
            <LoadingBlock className="flex-1" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FinancingCard() {
  const daysToExpired = useDaysToExpired();
  const hasValidSoftPull = useHasSoftPull();
  const hasExpiredSoftPull = useHasExpiredSoftPull();
  const isFinanceInitialized = useIsFinanceInitialized();
  const isLoggedIn = useIsLoggedIn();
  const status = getFinanceCardType(hasValidSoftPull, hasExpiredSoftPull);
  const isDefault = status === FinanceCardType.Default;
  const openSoftPullModal = useOpenSoftPullModal(
    SoftPullLocations.PriceAndPayment
  );

  const isFinanceLoaded = !isLoggedIn || isFinanceInitialized;

  const ctaProps: ButtonProps = {
    'data-analytics-id': isDefault
      ? 'Get pre-qualified'
      : 'Refresh expired terms',
    'data-qa': isDefault ? 'get-my-terms-link' : 'refresh-my-terms-link',
    onClick: _ => {
      openSoftPullModal();
    }
  };

  const CtaButton =
    status === FinanceCardType.Expired ? PrimaryButton : SecondaryButton;

  if (!isFinanceLoaded) return <LoadingCard />;

  return isDefault ? (
    <DefaultFinanceCard
      cta={
        <PrimaryButton
          className="h-40 min-h-40 w-full"
          mode="dark"
          {...ctaProps}
        >
          Get pre-qualified
        </PrimaryButton>
      }
    />
  ) : (
    <div
      className="rounded-md border border-solid border-grey-2 p-24 text-blue-6 -:bg-white"
      data-qa="preapproved-financing-card"
    >
      <CommonCard
        cta={
          <CtaButton
            className={cx('h-40 min-h-40 w-full uppercase', {
              hidden: hasValidSoftPull
            })}
            {...ctaProps}
          >
            refresh your terms
          </CtaButton>
        }
        daysToExpired={daysToExpired}
        status={status}
        title="Financing"
      />
    </div>
  );
}
